import DateJS, { format2DateStr, format2TimeStamp } from '@/utils/date';
import getRandomId from '@/utils/uuid';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import ReserveService from '@/api/reserve';
import { ReducerState, MainState, RoomSymbol } from '@/redux/reducers/reservation/reserveExchange';
import {
  RoomExchangeType,
  ExchangeRentRoomInfo,
  ExchangeRoomRole,
  ExchangeRoomPriceInfo,
} from '@/redux/reducers/reservation/base.d';
import { findItemByKey, flatPriceList } from '@/redux/actions/reservation/helper';
import { createTargetGetter, TargetType } from '@/components/Reservation/PopupTarget';

export const getStateFromOrder: (
  recNo: string,
  targetRoom?: Partial<ExchangeRentRoomInfo>,
) => Promise<MainState> = async (recNo, targetRoom) => {
  const res = await ReserveService.getReserveExchangeDetail(recNo);
  const orderInfo = res.data;
  const {
    roomTypeId,
    roomTypeName,
    roomPriceList,
    roomId,
    roomNo,
    wyuId,
    sourceChannelId,
    sourceChannelName,
    priceStrategyId,
    priceStrategyName,
    expectedArrivalTime,
    expectedDepartureTime,
    occDuration,
    resvNo,
    masterInfo,
    memberLevel,
  } = orderInfo;
  const originRoomPriceList: ExchangeRoomPriceInfo[] = [];
  const targetRoomPriceList: ExchangeRoomPriceInfo[] = [];
  (roomPriceList || []).forEach((item) => {
    const {
      rentDate,
      price,
      couponAmount,
      couponName,
      couponNo,
      couponType,
      couponWyuid,
      couponSubType,
      extensionId,
      occType,
      priceChannel,
    } = item;
    const date = format2DateStr(rentDate);
    const inputPrice = parseFen2Yuan(price);
    const couponValue = parseFen2Yuan(couponAmount);
    const itemPriceInfo = {
      rentDateId: getRandomId(),
      occType,
      rentDate: date,
      inputPrice,
      extensionId,
      couponAmount,
      couponName,
      couponNo,
      couponType,
      couponWyuid,
      couponSubType,
      couponValue,
      isBeforeAudit: false, // 默认都是未过夜审
      priceChannel,
    };
    originRoomPriceList.push({ ...itemPriceInfo });
    targetRoomPriceList.push({ ...itemPriceInfo });
  });

  const rentRoomInfos: ExchangeRentRoomInfo[] = [
    {
      indexId: getRandomId(),
      itemRole: ExchangeRoomRole.OriginRoom,
      roomTypeId,
      roomTypeName,
      roomId,
      roomNo,
      roomPriceList: originRoomPriceList,
    },
  ];

  // 20231020 默认换房房间
  if (targetRoom && targetRoom.roomId) {
    rentRoomInfos.push({
      indexId: getRandomId(),
      itemRole: ExchangeRoomRole.TargetRoom,
      roomTypeId: targetRoom.roomTypeId,
      roomTypeName: targetRoom.roomTypeName,
      roomId: targetRoom.roomId,
      roomNo: targetRoom.roomNo,
      roomPriceList: targetRoomPriceList,
    });
  } else {
    rentRoomInfos.push({
      indexId: getRandomId(),
      itemRole: ExchangeRoomRole.TargetRoom,
      roomTypeId,
      roomTypeName, // 默认同房型
      roomId: null, // 默认不选择房间
      roomNo: null,
      roomPriceList: targetRoomPriceList,
    });
  }
  return {
    commonInfo: {
      exchangeType: RoomExchangeType.KeepSame,
      mainOrderId: resvNo,
      recNo,
      ownerWYId: masterInfo?.wyuId || wyuId,
      memberLevel: masterInfo?.memberLevel || memberLevel,
    },
    channelInfo: {
      channelId: sourceChannelId,
      channelName: sourceChannelName,
      strategyId: priceStrategyId,
      strategyName: priceStrategyName,
    },
    rentTimeInfo: {
      occType: orderInfo.occType,
      expectedArrivalTime: DateJS(expectedArrivalTime),
      expectedDepartureTime: DateJS(expectedDepartureTime),
      businessDate: DateJS(originRoomPriceList[0].rentDate),
      rentTime: occDuration,
    },
    allRoomList: [],
    roomPriceList: [],
    marketPriceMap: {},
    rentRoomInfos,
  };
};

export const formatToSubmitData: (data: ReducerState) => { [key: string]: unknown } = (data) => {
  const { commonInfo, rentRoomInfos, channelInfo, roomPriceList } = data;
  const originRoomInfo = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.OriginRoom); // 房房间信息
  const targetRoomInfo = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.TargetRoom); // 新房间信息
  const isBlockRoom = targetRoomInfo.roomId === RoomSymbol.Block; // 是否是待排房
  const priceStrategyId = channelInfo.strategyId;
  const { cachePriceMap } = flatPriceList(roomPriceList);
  const strategyPrice = cachePriceMap[targetRoomInfo.roomTypeId] || {}; // 策略价格
  return {
    changePriceType: commonInfo.exchangeType === RoomExchangeType.KeepSame ? 0 : 1,
    destRoomId: isBlockRoom ? undefined : targetRoomInfo.roomId,
    destRoomNo: isBlockRoom ? undefined : targetRoomInfo.roomNo,
    destRoomTypeId: targetRoomInfo.roomTypeId,
    destRoomTypeName: targetRoomInfo.roomTypeName,
    recNo: commonInfo.recNo,
    roomChangeDayItemVO: (targetRoomInfo.roomPriceList || []).map((item) => {
      const {
        rentDate,
        inputPrice,
        couponAmount,
        couponNo,
        couponType,
        couponName,
        couponWyuid,
        couponSubType,
        extensionId,
      } = item;
      return {
        businessDate: format2TimeStamp(rentDate),
        priceStrategyId,
        strategyPrice: parseYuan2Fen(strategyPrice[rentDate]),
        price: parseYuan2Fen(inputPrice),
        couponAmount,
        couponNo,
        couponType,
        couponName,
        couponWyuid,
        couponSubType,
        extensionId,
      };
    }),
    srcRoomId: originRoomInfo.roomId,
    srcRoomName: originRoomInfo.roomNo,
    srcRoomTypeId: originRoomInfo.roomTypeId,
    srcRoomTypeName: originRoomInfo.roomTypeName,
  };
};

// 定位节点
export const getPopupContainer = createTargetGetter(TargetType.ReserveExchange);
